import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { Button, Input, Modal, Textarea } from '../../../components/UI'

const formatCurrency = (value) => new Intl.NumberFormat('es-CO', {
  style: 'currency',
  currency: 'COP',
  maximumFractionDigits: 0
}).format(Number(value || 0))

const normalizePhone = (value) => String(value || '').replace(/[^\d]/g, '')

const buildOrderMessage = ({ contactName, slug, customer, items }) => {
  const totalItems = items.reduce((sum, item) => sum + item.quantity, 0)
  const totalEstimate = items.reduce((sum, item) => sum + (Number(item.price || 0) * Number(item.quantity || 0)), 0)
  const lines = []

  lines.push('Hola, quiero realizar este pedido desde el catalogo.')
  if (contactName) {
    lines.push(`Negocio: ${contactName}`)
  }
  if (slug) {
    lines.push(`Catalogo: /catalogo/${slug}`)
  }

  lines.push('')
  lines.push('Datos del cliente')
  lines.push(`Nombre: ${customer.name}`)
  lines.push(`Telefono: ${customer.phone}`)
  lines.push(`WhatsApp: ${customer.whatsapp}`)
  lines.push(`Direccion: ${customer.address}`)

  lines.push('')
  lines.push('Pedido')
  items.forEach((item, index) => {
    const itemTotal = Number(item.price || 0) * Number(item.quantity || 0)
    const priceLabel = item.price ? ` | ${formatCurrency(item.price)} c/u` : ''
    const totalLabel = item.price ? ` | ${formatCurrency(itemTotal)} total` : ''
    lines.push(`${index + 1}. ${item.name} x${item.quantity}${priceLabel}${totalLabel}`)
  })

  lines.push('')
  lines.push(`Productos distintos: ${items.length}`)
  lines.push(`Cantidad total: ${totalItems}`)

  if (totalEstimate > 0) {
    lines.push(`Total estimado: ${formatCurrency(totalEstimate)}`)
  }

  return lines.join('\n')
}

export default function PublicCatalogCart({
  slug,
  contactPhone = '',
  contactName = '',
  isOpen,
  items,
  onClose,
  onChangeQuantity,
  onClear
}) {
  const [checkoutOpen, setCheckoutOpen] = useState(false)
  const [sending, setSending] = useState(false)
  const [error, setError] = useState('')
  const [customer, setCustomer] = useState({
    name: '',
    phone: '',
    address: '',
    whatsapp: ''
  })

  const totalItems = items.reduce((sum, item) => sum + item.quantity, 0)
  const totalEstimate = useMemo(
    () => items.reduce((sum, item) => sum + (Number(item.price || 0) * Number(item.quantity || 0)), 0),
    [items]
  )

  const openCheckout = () => {
    setError('')
    setCheckoutOpen(true)
  }

  const closeCheckout = () => {
    setCheckoutOpen(false)
    setError('')
  }

  const updateCustomer = (field, value) => {
    setCustomer((current) => ({ ...current, [field]: value }))
  }

  const sendToWhatsApp = async () => {
    if (!items.length) {
      setError('Agrega al menos un producto al carrito.')
      return
    }

    if (!contactPhone) {
      setError('Este catalogo no tiene un telefono de contacto configurado.')
      return
    }

    if (!customer.name.trim() || !customer.phone.trim() || !customer.address.trim() || !customer.whatsapp.trim()) {
      setError('Completa nombre, telefono, direccion y WhatsApp.')
      return
    }

    const waPhone = normalizePhone(contactPhone)
    if (!waPhone) {
      setError('El telefono de contacto configurado no es valido.')
      return
    }

    setSending(true)
    setError('')

    try {
      const message = buildOrderMessage({
        contactName,
        slug,
        customer,
        items
      })
      const url = `https://wa.me/${waPhone}?text=${encodeURIComponent(message)}`
      window.open(url, '_blank', 'noopener,noreferrer')
      closeCheckout()
    } catch {
      setError('No fue posible abrir WhatsApp.')
    } finally {
      setSending(false)
    }
  }

  return (
    <>
      <aside className={`fixed z-30 bg-white/95 border border-gray-200 shadow-2xl backdrop-blur-md overflow-hidden ${isOpen ? 'grid' : 'hidden'} right-4 top-4 w-[min(360px,calc(100vw-24px))] max-h-[calc(100vh-32px)] rounded-3xl grid-rows-[auto_1fr_auto] md:right-4 md:top-4 max-md:bottom-3 max-md:top-auto max-md:w-[calc(100vw-24px)] max-md:max-h-[72vh]`}>
        <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between gap-3">
          <div>
            <div className="uppercase tracking-[0.18em] text-[11px] text-gray-500">Carrito</div>
            <div className="text-lg font-extrabold text-gray-900">{totalItems} producto(s)</div>
          </div>
          <button type="button" onClick={onClose} className="w-9 h-9 rounded-full bg-gray-100 text-gray-900">
            x
          </button>
        </div>

        <div className="p-4 overflow-auto grid gap-3 content-start">
          {items.length ? items.map((item) => {
            const itemTotal = Number(item.price || 0) * Number(item.quantity || 0)

            return (
              <div key={item.id} className="border border-gray-200 rounded-2xl p-3 bg-white grid gap-2">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-sm font-bold text-gray-900 leading-snug">{item.name}</div>
                    <div className="text-xs text-gray-500">{item.categoryName || 'Sin categoria'}</div>
                    {item.price > 0 && (
                      <div className="mt-1 text-[11px] text-gray-500">
                        {formatCurrency(item.price)} c/u
                        {' '}
                        | {formatCurrency(itemTotal)} total
                      </div>
                    )}
                  </div>
                  <Link to={`/catalogo/${encodeURIComponent(slug)}/producto/${item.id}`} className="inline-flex items-center justify-center min-h-8 px-3 rounded-full bg-blue-600 text-white text-xs font-bold">
                    Ver
                  </Link>
                </div>
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="inline-flex items-center gap-2 border border-gray-200 rounded-full p-1 bg-white">
                    <button type="button" className="w-8 h-8 rounded-full bg-gray-100 font-bold" onClick={() => onChangeQuantity(item.id, item.quantity - 1)}>
                      -
                    </button>
                    <span className="min-w-6 text-center text-sm font-bold">{item.quantity}</span>
                    <button type="button" className="w-8 h-8 rounded-full bg-gray-100 font-bold" onClick={() => onChangeQuantity(item.id, item.quantity + 1)}>
                      +
                    </button>
                  </div>
                  <button type="button" className="inline-flex items-center justify-center min-h-8 px-3 rounded-full bg-gray-100 text-gray-900 text-xs font-bold" onClick={() => onChangeQuantity(item.id, 0)}>
                    Quitar
                  </button>
                </div>
              </div>
            )
          }) : (
            <div className="border border-dashed border-gray-200 rounded-2xl p-5 text-center text-sm text-gray-500 bg-gray-50/80">
              Todavia no hay productos en el carrito.
            </div>
          )}
        </div>

        <div className="border-t border-gray-200 px-5 py-4 grid gap-2 bg-gray-50/90">
          <div className="flex items-center justify-between text-sm text-gray-500">
            <span>Productos distintos</span>
            <strong className="text-base text-gray-900">{items.length}</strong>
          </div>
          <div className="flex items-center justify-between text-sm text-gray-500">
            <span>Cantidad total</span>
            <strong className="text-base text-gray-900">{totalItems}</strong>
          </div>
          {totalEstimate > 0 && (
            <div className="flex items-center justify-between text-sm text-gray-500">
              <span>Total estimado</span>
              <strong className="text-base text-gray-900">{formatCurrency(totalEstimate)}</strong>
            </div>
          )}
          <button
            type="button"
            onClick={openCheckout}
            disabled={!items.length || !contactPhone}
            className="inline-flex items-center justify-center min-h-10 px-4 rounded-full bg-emerald-600 text-white text-sm font-bold disabled:opacity-50"
          >
            Enviar pedido por WhatsApp
          </button>
          <button
            type="button"
            onClick={onClear}
            disabled={!items.length}
            className="inline-flex items-center justify-center min-h-10 px-4 rounded-full bg-blue-600 text-white text-sm font-bold disabled:opacity-50"
          >
            Vaciar carrito
          </button>
          {!contactPhone && (
            <div className="text-xs text-amber-700">
              El negocio aun no tiene un telefono de contacto configurado.
            </div>
          )}
        </div>
      </aside>

      <Modal
        isOpen={checkoutOpen}
        onClose={closeCheckout}
        title="Datos para enviar por WhatsApp"
        size="lg"
        footer={(
          <>
            <Button variant="secondary" onClick={closeCheckout} disabled={sending}>
              Cancelar
            </Button>
            <Button onClick={sendToWhatsApp} disabled={sending || !items.length || !contactPhone}>
              {sending ? 'Abriendo...' : 'Enviar a WhatsApp'}
            </Button>
          </>
        )}
      >
        <div className="space-y-4">
          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {error}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Nombre"
              value={customer.name}
              onChange={(event) => updateCustomer('name', event.target.value)}
              placeholder="Tu nombre completo"
              disabled={sending}
              required
            />
            <Input
              label="Telefono"
              value={customer.phone}
              onChange={(event) => updateCustomer('phone', event.target.value)}
              placeholder="Tu telefono"
              disabled={sending}
              required
            />
            <Input
              label="WhatsApp"
              value={customer.whatsapp}
              onChange={(event) => updateCustomer('whatsapp', event.target.value)}
              placeholder="Tu numero de WhatsApp"
              disabled={sending}
              required
            />
            <div className="md:col-span-2">
              <Textarea
                label="Direccion"
                value={customer.address}
                onChange={(event) => updateCustomer('address', event.target.value)}
                placeholder="Direccion de entrega"
                rows={3}
                disabled={sending}
                required
              />
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
            <div className="text-sm font-semibold text-slate-900">Destino del pedido</div>
            <div className="mt-1 text-sm text-slate-600">
              {contactName || 'Emprendedor'} {contactPhone ? `(${contactPhone})` : ''}
            </div>
            <div className="mt-3 text-sm text-slate-500">
              El mensaje se enviara a WhatsApp con un resumen estructurado del carrito y los datos del cliente.
            </div>
          </div>
        </div>
      </Modal>
    </>
  )
}
